import { useState } from "react";
import { Dealer } from "@/types/dealer";
import { PriceLockSection } from "@/components/price-lock/PriceLockSection";
import { DealerInfoCard } from "@/components/dealer-detail/DealerInfoCard";
import { HydratedDealerRatings } from "@/components/dealer-detail/HydratedDealerRatings";
import { QuickActionsCard } from "@/components/dealer-detail/QuickActionsCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DealerLocationMap } from "@/components/DealerLocationMap";
import { DealerQRCode } from "@/components/dealer-detail/DealerQRCode";
import { DealerSocialLinks } from "./DealerSocialLinks";
import { DealerReportButton } from "@/components/dealer-detail/DealerReportButton";
import { DealerReportModal } from "@/components/dealer-reports/DealerReportModal";
import { useDealerFavorite } from "@/components/dealer-card/useDealerFavorite";

interface DealerContentProps {
  dealer: Dealer | null;
  goldPrice: number | null;
  silverPrice: number | null;
  isAuthenticated: boolean;
  handleActivateDealer: () => void;
  handlePriceLock: (metal: "ORO" | "ARGENTO") => void;
  onMetalCardClick?: (metal: "gold" | "silver") => void;
  userDistance?: string | null;
}

export function DealerContent({
  dealer,
  goldPrice,
  silverPrice,
  isAuthenticated,
  handleActivateDealer,
  handlePriceLock,
  onMetalCardClick,
  userDistance
}: DealerContentProps) {
  const [selectedMetal, setSelectedMetal] = useState<'gold' | 'silver'>('gold');
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  
  // Hook per gestire i preferiti
  const { isFavorite, handleToggleFavorite, isToggling } = useDealerFavorite(dealer?.id || '');

  // Gestisce il click sui pulsanti di price lock nella QuickActionsCard
  const handlePriceLockClick = (metal: "ORO" | "ARGENTO") => {
    const metalType = metal === "ORO" ? 'gold' : 'silver';
    setSelectedMetal(metalType);
    
    // Scroll alla sezione price lock senza aprire modali
    setTimeout(() => {
      const priceLockSection = document.getElementById('price-lock-section');
      if (priceLockSection) {
        const elementPosition = priceLockSection.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - 50;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  // Gestisce il click sulle card oro e argento nella DealerInfoCard
  const handleMetalCardClick = (metal: 'gold' | 'silver') => {
    setSelectedMetal(metal);
    
    // Scroll alla sezione price lock
    setTimeout(() => {
      const priceLockSection = document.getElementById('price-lock-section');
      if (priceLockSection) {
        const elementPosition = priceLockSection.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - 50;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  // If dealer is null, render a loading state
  if (!dealer) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Caricamento...</CardTitle>
            </CardHeader>
            <CardContent>
              <p>Caricamento dei dati del commerciante in corso...</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }
  
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <DealerInfoCard 
          dealer={dealer}
          goldPrice={goldPrice}
          silverPrice={silverPrice}
          isAuthenticated={isAuthenticated}
          handleActivateDealer={handleActivateDealer}
          onMetalCardClick={handleMetalCardClick}
          userDistance={userDistance}
        />
        
        {dealer.activated && (
          <PriceLockSection 
            dealerId={dealer.id} 
            dealerName={dealer.name} 
            dealerSpread={dealer.buySpread}
            initialMetal={selectedMetal}
          />
        )}
        
        <HydratedDealerRatings dealerId={dealer.id} dealerName={dealer.name} />
      </div>
      
      <div className="space-y-6">
        <div className="space-y-4">
          <QuickActionsCard 
            phone={dealer.phone} 
            website={dealer.website}
            handlePriceLock={handlePriceLockClick}
            handleActivateDealer={handleActivateDealer}
            dealerActivated={dealer.activated}
            dealerId={dealer.id}
            citySlug={dealer.city ? dealer.city.toLowerCase().replace(/\s+/g, '-') : ''}
            isAuthenticated={isAuthenticated}
            isFavorite={isFavorite}
            handleToggleFavorite={handleToggleFavorite}
            isToggling={isToggling}
          />
        </div>
        
        <Card>
          <CardHeader>
            <CardTitle>Posizione</CardTitle>
          </CardHeader>
          <CardContent>
            <DealerLocationMap 
              latitude={dealer.latitude} 
              longitude={dealer.longitude} 
              name={dealer.name}
              address={dealer.address || 'Indirizzo non disponibile'}
              mapId={`dealer-map-${dealer.id}`}
            />
            <p className="text-xs text-muted-foreground mt-2 text-center">
              {dealer.address} {dealer.postal_code && `- ${dealer.postal_code}`}
            </p>
            {userDistance && (
              <p className="text-xs text-muted-foreground mt-1 text-center font-medium">
                📍 Distanza: {userDistance}
              </p>
            )}
          </CardContent>
        </Card>
        
        <DealerSocialLinks
          facebook={dealer.facebook}
          instagram={dealer.instagram}
          twitter={dealer.twitter}
          youtube={dealer.youtube}
          linkedin={dealer.linkedin}
        />

        <DealerQRCode 
          dealerId={dealer.id} 
          dealerName={dealer.name}
          city={dealer.city}
        />

        <DealerReportButton 
          onReportClick={() => setIsReportModalOpen(true)}
        />
      </div>

      <DealerReportModal
        dealerId={dealer.id}
        dealerName={dealer.name}
        dealerAddress={dealer.address}
        isOpen={isReportModalOpen}
        onOpenChange={setIsReportModalOpen}
      />
    </div>
  );
}
